//
// Copyright (c) 2024 Emplifi, Inc.
//

import {
  ButtonView,
  clickOutsideHandler,
  ContextualBalloon,
  Dialog,
  type Editor,
  Locale,
  Plugin,
  View,
} from "ckeditor5";
// @ts-ignore
import i18next from "i18n/i18n";
// @ts-ignore
import Messenger from "messaging/messenger";
// @ts-ignore
import snippetIcon from "img/svg/add_snippet.svg";
import { SnippetView, type SnippetViewType } from "./ui/snippetView";
import SnippetCommand from "./snippetCommand";
import { getSnippetWidgetDocument } from "./util";
import "./css/openSnippetWarning.css";

export default class SnippetUi extends Plugin {
  declare private balloon: ContextualBalloon;
  declare private snippetView: SnippetViewType | null;

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "SnippetUi";
  }

  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [ContextualBalloon];
  }

  init() {
    this._addToolbarButtons();
    this.balloon = this.editor.plugins.get(ContextualBalloon);
  }

  destroy() {
    super.destroy();
    if (this.snippetView) {
      this.snippetView.destroy();
      this.snippetView = null;
    }
  }

  private get _isSnippetViewInPanel(): boolean {
    if (!this.snippetView) {
      return false;
    }
    return this.balloon.hasView(this.snippetView);
  }

  private _addOpenSnippetEditorButton(editor: Editor, locale: Locale): ButtonView {
    const button = new ButtonView(locale);
    button.set({
      isEnabled: true,
      label: i18next.t("ckeditor.snippet.openSnippetEditor"),
      withText: true,
    });
    this.listenTo(button, "execute", () => this._showOpenSnippetEditorDialog(editor, locale));
    return button;
  }

  private _addSnippetView() {
    this.snippetView = this._createSnippetView();
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    this.snippetView!.disableCssTransitions();
    this.balloon.add({
      view: this.snippetView!,
      position: {
        target: () => {
          return view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange()!);
        },
      },
    });
    if (this.balloon.visibleView == this.snippetView) {
      this.snippetView!.focus();
    }
    this.snippetView!.enableCssTransitions();
  }

  private _addToolbarButtons() {
    const editor = this.editor;
    editor.ui.componentFactory.add("snippet", (locale) => {
      const command = editor.commands.get("snippet") as SnippetCommand;
      const button = new ButtonView(locale);
      button.set({
        isEnabled: true,
        label: i18next.t("ckeditor.snippet.buttonName"),
        icon: snippetIcon,
        tooltip: true,
      });
      button.bind("isEnabled").to(command);
      this.listenTo(button, "execute", () => this._showUi());
      return button;
    });
    if (!editor.config.get("snippet.skipOpenSnippetEditor")) {
      editor.ui.componentFactory.add("openSnippetEditor", (locale: Locale) =>
        this._addOpenSnippetEditorButton(editor, locale),
      );
    }
  }

  private _cancelUi() {
    const command = this.editor.commands.get("snippet") as SnippetCommand;
    command.refresh();
    this._closeSnippetView();
  }

  private _closeSnippetView() {
    if (this._isSnippetViewInPanel) {
      this.balloon.remove(this.snippetView!);
      // @ts-ignore
      this.snippetView.destroy();
      this.editor.editing.view.focus();
    }
  }

  private _createSnippetView(): SnippetViewType {
    const editor = this.editor;
    const command = editor.commands.get("snippet") as SnippetCommand;
    const snippetView = new SnippetView(editor, command);
    snippetView.on("submit", () => {
      editor.execute("snippet", {
        snippetId: this.snippetView!.snippetId,
        snippetName: this.snippetView!.snippetName,
      });
      this._closeSnippetView();
    });
    snippetView.on("cancel", () => {
      this._cancelUi();
    });
    clickOutsideHandler({
      emitter: snippetView,
      activator: () => this._isSnippetViewInPanel,
      contextElements: [this.balloon.view.element!],
      callback: () => this._cancelUi(),
    });
    return snippetView as SnippetViewType;
  }

  private _openSnippetEditor(snippetId: string) {
    Messenger.reqres.request("ckeditor:editSnippet", snippetId);
  }

  private _showOpenSnippetEditorDialog(editor: Editor, locale: Locale) {
    const selection = editor.model.document.selection;
    const snippetId = getSnippetWidgetDocument(selection)?.getAttribute("snippetId") as
      | string
      | undefined;
    if (!snippetId) {
      return;
    }
    const dialog: Dialog = editor.plugins.get(Dialog);
    const messageView = new View(locale);
    messageView.setTemplate({
      tag: "div",
      attributes: {
        class: ["open-snippet-message"],
      },
      children: [
        {
          tag: "div",
          children: [i18next.t("ckeditor.snippet.openSnippetEditorWarning")],
        },
        {
          tag: "div",
          children: [i18next.t("ckeditor.snippet.openSnippetEditorNote")],
        },
      ],
    });
    dialog.show({
      id: "openSnippetEditorWarning",
      actionButtons: [
        {
          label: i18next.t("common.cancel"),
          class: "ck-button-cancel",
          withText: true,
          onExecute: () => dialog.hide(),
        },
        {
          label: i18next.t("ckeditor.snippet.openSnippetEditor"),
          class: "ck-button-action",
          withText: true,
          onExecute: () => {
            dialog.hide();
            this._openSnippetEditor(snippetId);
          },
        },
      ],
      className: "open-snippet-dialog",
      content: messageView,
      isModal: true,
      title: i18next.t("ckeditor.snippet.openSnippetEditor"),
    });
  }

  private _showUi() {
    this._addSnippetView();
    this.balloon.showStack("main");
  }
}
